import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  formatAddonPackLabel,
  formatMoney,
} from "@/lib/billingPlanDisplay";
import { billingService } from "@/services/billingService";
import type { BillingAddonCatalogItem } from "@/types/billing";

type Props = {
  pack: BillingAddonCatalogItem;
  disabled: boolean;
  busyLabel: string | null;
  onConfirm: () => void;
};

const renewalDate = (value?: string | null) => {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString();
};

export const StorageAddonDialog = ({
  pack,
  disabled,
  busyLabel,
  onConfirm,
}: Props) => {
  const [open, setOpen] = useState(false);

  const quoteQuery = useQuery({
    queryKey: ["billing", "storage-quote", pack.price_id],
    queryFn: () => billingService.getStorageAddonQuote(pack.price_id),
    enabled: open,
    staleTime: 30_000,
  });

  const quote = quoteQuery.data;
  const prorated = formatMoney(quote?.prorated_amount, quote?.currency);
  const full = formatMoney(quote?.full_amount, quote?.currency);
  const renews = renewalDate(quote?.current_period_end);
  const triggerLabel =
    busyLabel ?? formatAddonPackLabel(pack, { includePeriod: true });

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          className="h-auto w-full flex-col items-start justify-start gap-1 whitespace-normal py-3 text-left"
          disabled={disabled}
        >
          <span>{triggerLabel}</span>
          {Number(pack.grant_storage_gb) > 0 ? (
            <span className="text-xs font-normal text-muted-foreground">
              {Number(pack.grant_storage_gb).toLocaleString()} GB
            </span>
          ) : null}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Add {pack.name}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              {quoteQuery.isLoading ? (
                <p>Working out what you owe for the rest of this period…</p>
              ) : quoteQuery.isError || !prorated ? (
                <p>
                  We could not price this right now. You can still continue — an
                  invoice will be raised for the days remaining in this period,
                  and the storage is added once you pay it.
                </p>
              ) : (
                <>
                  <p className="text-base font-medium text-foreground">
                    {prorated} to pay now
                  </p>
                  <p>
                    Prorated for the {quote?.days_remaining ?? 0} day
                    {quote?.days_remaining === 1 ? "" : "s"} left in this
                    billing period
                    {renews ? `, which ends on ${renews}` : ""}.
                    {full
                      ? ` The full price is ${full} per ${quote?.period ?? "period"}, charged at each renewal from then on.`
                      : ""}
                  </p>
                </>
              )}
              <p>
                We will raise an invoice and take you to Airwallex to pay it.
                The storage is added once the payment clears. Removing it later
                does not refund the remaining days.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Not now</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>
            Continue to payment
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default StorageAddonDialog;
